import { useRestaurant } from '@local/do-secundo-restaurant-provider'
import {
  Formats,
  format,
  isToday,
  isTomorrow
} from '@toasttab/buffet-pui-date-utilities'
import { getRxDateFromISO } from '../utils/time-utils'
import { DateTime } from 'luxon'

interface UseDate {
  toRelativeDateString: (isoDate: string | undefined) => string
  toFormatedHours: (isoDate: string | undefined, showOffset?: boolean) => string
  toShortDateString: (isoDate: string) => string
  toFormattedLocalDate: (localDate: string) => string
  toFormattedTimeRange: (
    range: { time?: string; endTime?: string } | undefined
  ) => string
}

export const useDate = (): UseDate => {
  const { locale, restaurantInfo } = useRestaurant()
  const timeZoneId = restaurantInfo?.timeZoneId

  const formatDate = (date: Date, formatString: string) => {
    return format(date, formatString, locale)
  }

  // Pre-configured formatters with locale and timezone
  const toRelativeDateString = (isoDate: string | undefined) => {
    const date = getRxDateFromISO(isoDate, timeZoneId)
    if (!date) return ''

    if (isToday(date)) {
      return 'Today'
    }
    if (isTomorrow(date)) {
      return 'Tomorrow'
    }
    return formatDate(date, Formats.date.medium_without_year) ?? ''
  }

  const toFormatedHours = (isoDate: string | undefined, showOffset = true) => {
    const date = getRxDateFromISO(isoDate, timeZoneId)
    if (!date) return ''

    const guestTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const showRestaurantTimeZone = showOffset && guestTimeZone !== timeZoneId

    // Format the time portion without timezone
    const timeString = format(date, Formats.time.short, locale) ?? ''

    if (showRestaurantTimeZone && timeZoneId) {
      const restaurantTzName = format(date, Formats.timeZone.short, {
        locale: locale,
        timeZone: timeZoneId
      })

      return restaurantTzName ? `${timeString} ${restaurantTzName}` : timeString
    }

    return timeString
  }

  const toShortDateString = (isoDate: string) => {
    const date = getRxDateFromISO(isoDate, timeZoneId)
    if (!date) return ''

    return formatDate(date, Formats.date.short_without_year) ?? ''
  }

  const toFormattedLocalDate = (localDate: string) => {
    const dateTime = DateTime.fromISO(localDate)
    if (!dateTime.isValid) return ''

    // Convert Luxon DateTime to JavaScript Date without timezone conversion
    const jsDate = dateTime.toJSDate()
    return formatDate(jsDate, Formats.date.short_without_year) ?? ''
  }

  const toFormattedTimeRange = (
    range: { time?: string; endTime?: string } | undefined
  ) => {
    if (!range || !range.time) return ''

    if (range.endTime) {
      return `${toFormatedHours(range.time, false)} - ${toFormatedHours(
        range.endTime,
        false
      )}`
    }

    return toFormatedHours(range.time, false)
  }

  return {
    toRelativeDateString,
    toFormatedHours,
    toShortDateString,
    toFormattedLocalDate,
    toFormattedTimeRange
  }
}
